import { lazy, Suspense } from "react";

import Stack from "@mui/material/Stack";
import { Paths } from "configs";
import { RouteProps } from "react-router-dom";

const HomePage = lazy(() => import("pages/Home"));

// Payroll setting
const LeavePolicyPage = lazy(() => import("pages/PayrollSetting/LeavePolicy"));
const HolidayPage = lazy(() => import("pages/PayrollSetting/Holiday"));
const LeaveTypePage = lazy(() => import("pages/PayrollSetting/LeaveType"));
const AllowancePage = lazy(() => import("pages/PayrollSetting/Allowance"));
const WorkShiftPage = lazy(() => import("pages/PayrollSetting/WorkShift"));
const CompanySettingPage = lazy(
  () => import("pages/PayrollSetting/CompanySetting")
);
const CompensationPage = lazy(
  () => import("pages/PayrollSetting/Compensation")
);
const ApprovalWorkFlowPage = lazy(
  () => import("pages/PayrollSetting/ApprovalWorkFlow")
);

const PayrollTemplatePage = lazy(
  () => import("pages/PayrollSetting/PayrollTemplate")
);
const EarningPage = lazy(() => import("pages/PayrollSetting/Earning"));
const BenefitsInKindPage = lazy(
  () => import("pages/PayrollSetting/BenefitsInKind")
);
const OTRatePage = lazy(() => import("pages/PayrollSetting/OTRate"));
const PayrollCycle = lazy(() => import("pages/PayrollSetting/PayrollCycle"));
const EmailTemplatePage = lazy(
  () => import("pages/PayrollSetting/EmailTemplate")
);
const PayslipTemplatePage = lazy(
  () => import("pages/PayrollSetting/PayslipTemplate")
);

// Master data
const EthnicityPage = lazy(() => import("pages/MasterData/Ethnicity"));
const CountryPage = lazy(() => import("pages/MasterData/Country"));
const CurrencyPage = lazy(() => import("pages/MasterData/Currency"));
const DistrictPage = lazy(() => import("pages/MasterData/District"));
const ProvincePage = lazy(() => import("pages/MasterData/Province"));
const WardPage = lazy(() => import("pages/MasterData/Ward"));
const PITRatePage = lazy(() => import("pages/MasterData/PITRate"));
const InsurancePage = lazy(() => import("pages/MasterData/Insurance"));
const DeductionPage = lazy(() => import("pages/MasterData/Deduction"));
const TimezonePage = lazy(() => import("pages/MasterData/Timezone"));
const BankPage = lazy(() => import("pages/MasterData/Bank"));
const RegionalMinimumWagePage = lazy(
  () => import("pages/MasterData/RegionalMinimumWage")
);

// Company
const CompanyInfoPage = lazy(() => import("pages/Company/BasicInfo"));
const WorkLevelPage = lazy(() => import("pages/Company/WorkLevel"));
const ContractTypePage = lazy(() => import("pages/Company/ContractType"));
const DepartmentPage = lazy(() => import("pages/Company/Department"));
const BranchPage = lazy(() => import("pages/Company/Branch"));
const TitlePage = lazy(() => import("pages/Company/Title"));
const OrgChartPage = lazy(() => import("pages/Company/OrgChart"));
const RoleAndPermissionPage = lazy(
  () => import("pages/Company/RoleAndPermission")
);

const KPISettingPage = lazy(() => import("pages/Company/KPISetting"));
const TimeAttendancePage = lazy(() => import("pages/TimeAttendance"));

const RunPayrollPage = lazy(() => import("pages/RunPayroll"));
const PayrollPage = lazy(() => import("pages/Payroll"));
const EmployeePage = lazy(() => import("pages/Employee"));
const EmployeeDetailPage = lazy(() => import("pages/Employee/CRU"));

// Report
const MyReportPage = lazy(() => import("pages/Report/page"));
const ReportTimesheetPage = lazy(() => import("pages/Report/Timesheet"));
const ReportReconcilePage = lazy(() => import("pages/Report/Reconcile"));
const PayrollSummaryPage = lazy(() => import("pages/Report/PayrollSummary"));
const SHUIContributionPage = lazy(
  () => import("pages/Report/SHUIContribution")
);
const ReportPITSummaryPage = lazy(() => import("pages/Report/PITSummary"));
const ReportOneStreamPage = lazy(() => import("pages/Report/OneStream"));
const ReportEmployeeProfilePage = lazy(
  () => import("pages/Report/EmployeeProfile")
);
const ReportDiligencePage = lazy(() => import("pages/Report/Diligence"));
const Report13ThMonthSalaryPage = lazy(
  () => import("pages/Report/13ThMonthSalary")
);
const RoleDetailPage = lazy(
  () => import("pages/Company/RoleAndPermission/Role/CRU/page")
);
const GroupEmployeeDetailPage = lazy(
  () => import("pages/Company/RoleAndPermission/GroupEmployee/CRU/page")
);

const SalaryAdvancePage = lazy(() => import("pages/SalaryAdvance"));
const SalaryAdvanceDetailPage = lazy(
  () => import("pages/SalaryAdvance/CRU/detail")
);
const AuditLogPage = lazy(() => import("pages/AuditLog"));

// Adjustment
const AdjustmentPage = lazy(() => import("pages/Adjustment"));
// PIT
const PITPage = lazy(() => import("pages/PIT"));

// Product activity
const PAMachinePage = lazy(() => import("pages/Productivity/Machine"));
const PAProductPage = lazy(() => import("pages/Productivity/Product"));
const PAMachineGroupPage = lazy(
  () => import("pages/Productivity/MachineGroup")
);
const PAReasonStopPage = lazy(() => import("pages/Productivity/ReasonStop"));
const PAStandardQuantityPage = lazy(
  () => import("pages/Productivity/StandardQuantity")
);
const PATemplatePage = lazy(() => import("pages/Productivity/Template"));
const PAProductionLogPage = lazy(
  () => import("pages/Productivity/OTData/Sections/ProductionLog")
);
const PAMachineDowntimePage = lazy(
  () => import("pages/Productivity/OTData/Sections/OTMachineDowntime")
);
const PAOTWithoutMachineOperationPage = lazy(
  () => import("pages/Productivity/OTData/Sections/OTWithoutMachineOperation")
);
const PARunProductivityPage = lazy(
  () => import("pages/Productivity/RunProductivity")
);
// Bảng lương năng suất
const PAProductivityManagePage = lazy(
  () => import("pages/Productivity/ProductivitySalary")
);
const PAProductivitySalarySummaryPage = lazy(
  () => import("pages/Productivity/ProductivitySalarySummary/page")
);

const KpiIndividualPage = lazy(() => import("pages/KpiIndividual"));
const KpiDepartmentPage = lazy(() => import("pages/KpiDepartment"));

export const DashboardSuspenseWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Suspense
    fallback={
      <Stack
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <div className="spinnerSetting" />
      </Stack>
    }
  >
    {children}
  </Suspense>
);

export const DashboardRoutes: Array<
  RouteProps & {
    useSuspense?: boolean;
    checkPermission?: boolean;
  }
> = [
  // #region Dashboard
  {
    path: Paths.App,
    index: true,
    element: <HomePage />,
    useSuspense: true,
  },
  // #endregion
  // #region Company
  {
    path: Paths.WorkLevel,
    index: true,
    element: <WorkLevelPage />,
    useSuspense: true,
  },
  {
    path: Paths.KPISetting,
    index: true,
    element: <KPISettingPage />,
    useSuspense: true,
  },
  {
    path: Paths.CompanyInfo,

    index: true,
    element: <CompanyInfoPage />,
    useSuspense: true,
  },
  {
    path: Paths.ContractType,
    index: true,
    element: <ContractTypePage />,
    useSuspense: true,
  },
  {
    path: Paths.Department,
    index: true,
    element: <DepartmentPage />,
    useSuspense: true,
  },
  {
    path: Paths.Branch,
    index: true,
    element: <BranchPage />,
    useSuspense: true,
  },
  {
    path: Paths.Title,
    index: true,
    element: <TitlePage />,
    useSuspense: true,
  },
  {
    path: Paths.OrgChart,
    index: true,
    element: <OrgChartPage />,
    useSuspense: true,
  },
  {
    path: Paths.RoleAndPermission,
    index: true,
    element: <RoleAndPermissionPage />,
    useSuspense: true,
  },
  {
    path: `${Paths.RoleDetail}/:id`,
    index: true,
    element: <RoleDetailPage />,
    useSuspense: true,
  },
  {
    path: `${Paths.RoleDetail}`,
    index: true,
    element: <RoleDetailPage />,
    useSuspense: true,
  },
  {
    path: `${Paths.GroupEmployeeDetail}/:id`,
    index: true,
    element: <GroupEmployeeDetailPage />,
    useSuspense: true,
  },
  {
    path: `${Paths.GroupEmployeeDetail}`,
    index: true,
    element: <GroupEmployeeDetailPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Payroll Setting
  {
    path: Paths.LeavePolicy,
    index: true,
    element: <LeavePolicyPage />,
    useSuspense: true,
  },
  {
    path: Paths.Holiday,
    index: true,
    element: <HolidayPage />,
    useSuspense: true,
  },
  {
    path: Paths.LeaveType,
    index: true,
    element: <LeaveTypePage />,
    useSuspense: true,
  },
  {
    path: Paths.Allowance,
    index: true,
    element: <AllowancePage />,
    useSuspense: true,
  },
  {
    path: Paths.WorkShift,
    index: true,
    element: <WorkShiftPage />,
    useSuspense: true,
  },
  {
    path: Paths.Compensation,
    index: true,
    element: <CompensationPage />,
    useSuspense: true,
  },
  {
    path: Paths.PayrollTemplate,
    index: true,
    element: <PayrollTemplatePage />,
    useSuspense: true,
  },
  {
    path: Paths.Earning,
    index: true,
    element: <EarningPage />,
    useSuspense: true,
  },
  {
    path: Paths.BenefitsInKind,
    index: true,
    element: <BenefitsInKindPage />,
    useSuspense: true,
  },
  {
    path: Paths.CompanySetting,
    index: true,
    element: <CompanySettingPage />,
    useSuspense: true,
  },
  {
    path: Paths.OTRate,
    index: true,
    element: <OTRatePage />,
    useSuspense: true,
  },
  {
    path: Paths.ApprovalWorkflow,
    index: true,
    element: <ApprovalWorkFlowPage />,
    useSuspense: true,
  },
  {
    path: Paths.PayrollCycle,
    index: true,
    element: <PayrollCycle />,
    useSuspense: true,
  },
  {
    path: Paths.EmailTemplate,
    index: true,
    element: <EmailTemplatePage />,
    useSuspense: true,
  },
  {
    path: Paths.PayslipTemplate,
    index: true,
    element: <PayslipTemplatePage />,
    useSuspense: true,
  },
  // #endregion
  // #region Master data
  {
    path: Paths.Insurance,
    index: true,
    element: <InsurancePage />,
    useSuspense: true,
  },
  {
    path: Paths.RegionalMinimumWage,
    index: true,
    element: <RegionalMinimumWagePage />,
    useSuspense: true,
  },
  {
    path: Paths.PITRate,
    index: true,
    element: <PITRatePage />,
    useSuspense: true,
  },
  {
    path: Paths.Deduction,
    index: true,
    element: <DeductionPage />,
    useSuspense: true,
  },
  {
    path: Paths.Currency,
    element: <CurrencyPage />,
    useSuspense: true,
  },
  {
    path: Paths.Ethnicity,
    element: <EthnicityPage />,
    useSuspense: true,
  },
  {
    path: Paths.Country,
    element: <CountryPage />,
    useSuspense: true,
  },
  {
    path: Paths.Province,
    element: <ProvincePage />,
    useSuspense: true,
  },
  {
    path: Paths.District,
    element: <DistrictPage />,
    useSuspense: true,
  },
  {
    path: Paths.Ward,
    element: <WardPage />,
    useSuspense: true,
  },
  {
    path: Paths.Timezone,
    element: <TimezonePage />,
    useSuspense: true,
  },
  {
    path: Paths.Bank,
    element: <BankPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Time Attendance
  {
    path: Paths.TimeAttendance,
    element: <TimeAttendancePage />,
    useSuspense: true,
  },
  // #endregion
  // #region Run Payroll
  {
    path: Paths.RunPayroll,
    element: <RunPayrollPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Payroll
  {
    path: Paths.Payroll,
    element: <PayrollPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Employee
  {
    path: Paths.Employee,
    element: <EmployeePage />,
    useSuspense: true,
  },
  // #endregion
  // #region Employee Create
  {
    path: `${Paths.EmployeeDetail}/create`,
    element: <EmployeeDetailPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Employee Detail
  {
    path: `${Paths.EmployeeDetail}/:id`,
    element: <EmployeeDetailPage />,
    useSuspense: true,
  },
  {
    path: Paths.Report,
    element: <MyReportPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportTimesheet,
    element: <ReportTimesheetPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportReconcile,
    element: <ReportReconcilePage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportPayrollSummary,
    element: <PayrollSummaryPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportSHUIContribution,
    element: <SHUIContributionPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportPITSummary,
    element: <ReportPITSummaryPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportOneStream,
    element: <ReportOneStreamPage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportEmployeeProfile,
    element: <ReportEmployeeProfilePage />,
    useSuspense: true,
  },
  {
    path: Paths.ReportDiligence,
    element: <ReportDiligencePage />,
    useSuspense: true,
  },
  {
    path: Paths.Report13thMonthSalary,
    element: <Report13ThMonthSalaryPage />,
    useSuspense: true,
  },
  // #endregion
  // #region Adjustment
  {
    path: Paths.Adjustment,
    element: <AdjustmentPage />,
    useSuspense: true,
  },
  {
    path: Paths.PIT,
    element: <PITPage />,
    useSuspense: true,
  },
  // #endregion
  {
    path: Paths.SalaryAdvance,
    element: <SalaryAdvancePage />,
    useSuspense: true,
  },
  {
    path: `${Paths.SalaryAdvanceDetail}/:id`,
    element: <SalaryAdvanceDetailPage />,
    useSuspense: true,
  },
  {
    path: `${Paths.SalaryPaymentDetail}/:id`,
    element: <SalaryAdvanceDetailPage />,
    useSuspense: true,
  },
  {
    path: Paths.AuditLog,
    element: <AuditLogPage />,
    useSuspense: true,
  },
  // Product activity
  {
    path: Paths.PAMachine,
    element: <PAMachinePage />,
    useSuspense: true,
  },
  {
    path: Paths.PAProduct,
    element: <PAProductPage />,
    useSuspense: true,
  },
  {
    path: Paths.PAMachineGroup,
    element: <PAMachineGroupPage />,
    useSuspense: true,
  },
  {
    path: Paths.PAReasonStop,
    element: <PAReasonStopPage />,
    useSuspense: true,
  },
  {
    path: Paths.PAStandardQuantity,
    element: <PAStandardQuantityPage />,
    useSuspense: true,
  },
  {
    path: Paths.PATemplate,
    element: <PATemplatePage />,
    useSuspense: true,
  },
  {
    path: Paths.PAProductionLogs,
    element: <PAProductionLogPage />,
    useSuspense: true,
  },
  {
    path: Paths.PAMachineDowntime,
    element: <PAMachineDowntimePage />,
    useSuspense: true,
  },
  {
    path: Paths.PAOTWithoutMachineOperations,
    element: <PAOTWithoutMachineOperationPage />,
    useSuspense: true,
  },
  {
    path: Paths.PARunProductivity,
    element: <PARunProductivityPage />,
    useSuspense: true,
  },
  {
    path: Paths.PAProductivitySalary,
    element: <PAProductivityManagePage />,
    useSuspense: true,
  },
  {
    path: Paths.PAProductivitySalarySummary,
    element: <PAProductivitySalarySummaryPage />,
    useSuspense: true,
  },
  {
    path: Paths.KPIIndividual,
    element: <KpiIndividualPage />,
    useSuspense: true,
  },
  {
    path: Paths.KPIDepartment,
    element: <KpiDepartmentPage />,
    useSuspense: true,
  },
];
